// Component: ModelVersionSelect.jsx
// Purpose: Scope model_regime nodes to promoted registry versions while preserving saved labels.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { services } from '../../../../../services/api';
import { unwrapModelVersions } from '../../../../../services/modelVersionsEnvelope';

const SELECT_CLASS = 'w-full px-3 py-2.5 rounded-lg border border-[var(--divider)] bg-bg-2 font-mono text-sm font-medium focus:ring-2 focus:ring-[color-mix(in_oklch,var(--color-accent)_50%,transparent)] outline-none';

const ModelVersionSelect = ({
    value = '',
    instrumentId,
    instrumentLabel,
    timeframe,
    onChange,
}) => {
    const [options, setOptions] = useState([]);
    const [isLoading, setIsLoading] = useState(Boolean(instrumentId && timeframe));
    const [hasError, setHasError] = useState(false);
    const requestEpoch = useRef(0);
    const scopeKeyRef = useRef('');
    //! value is read through a ref so a selection change never refetches (R7-T2); only scope does.
    const valueRef = useRef(value);
    valueRef.current = value;

    useEffect(() => {
        const epoch = ++requestEpoch.current;

        if (!instrumentId || !timeframe) {
            setOptions([]);
            setIsLoading(false);
            setHasError(false);
            return () => {
                if (requestEpoch.current === epoch) requestEpoch.current += 1;
            };
        }

        const scopeKey = `${instrumentId}|${timeframe}`;
        if (scopeKeyRef.current !== scopeKey) setOptions([]);
        scopeKeyRef.current = scopeKey;
        setIsLoading(true);
        setHasError(false);

        const selectedId = valueRef.current;
        const params = {
            view: 'picker',
            status: 'promoted',
            instrument_id: instrumentId,
            timeframe,
            ...(selectedId ? { selected_id: selectedId } : {}),
        };

        services.models.listVersions(params)
            .then((response) => {
                if (requestEpoch.current !== epoch) return;
                setOptions(unwrapModelVersions(response).versions);
                setIsLoading(false);
            })
            .catch(() => {
                if (requestEpoch.current !== epoch) return;
                setOptions([]);
                setHasError(true);
                setIsLoading(false);
            });

        return () => {
            if (requestEpoch.current === epoch) requestEpoch.current += 1;
        };
    }, [instrumentId, timeframe]);

    const hasSelectableVersion = useMemo(
        () => options.some((option) => option.selectable !== false),
        [options],
    );
    // AC7.4/7.5: a saved value with no matching option (loading, missing scope, fetch error,
    // out of scope) renders as a disabled placeholder - never a blank select.
    const hasMatchingOption = options.some((option) => option.id === value);
    const needsPlaceholder = Boolean(value) && !hasMatchingOption;
    const placeholderLabel = isLoading
        ? `Saved model ${value} (loading…)`
        : hasError
            ? `Saved model ${value} (unable to load label)`
            : !instrumentId || !timeframe
                ? `Saved model ${value} (select instrument and timeframe)`
                : "Saved model (not in this strategy's instrument/timeframe scope)";
    const scopeLabel = instrumentLabel || instrumentId || 'selected instrument';

    return (
        <div className="space-y-1.5">
            <select
                aria-label="Model version"
                className={SELECT_CLASS}
                value={value || ''}
                disabled={!instrumentId || !timeframe || isLoading || hasError}
                onChange={(event) => {
                    const nextId = event.target.value;
                    onChange(nextId, options.find((option) => option.id === nextId));
                }}
            >
                {!value && (
                    <option value="" disabled>
                        {isLoading ? 'Loading promoted models…' : 'Select a promoted model'}
                    </option>
                )}
                {needsPlaceholder && (
                    <option value={value} disabled>
                        {placeholderLabel}
                    </option>
                )}
                {options.map((option) => (
                    <option
                        key={option.id}
                        value={option.id}
                        disabled={option.selectable === false}
                    >
                        {option.version_label}
                    </option>
                ))}
            </select>

            {!instrumentId || !timeframe ? (
                <p className="text-[10px] text-fg-3">Select a strategy instrument and timeframe.</p>
            ) : hasError ? (
                <p role="alert" className="text-[10px] text-red-500">Unable to load promoted models.</p>
            ) : !isLoading && !hasSelectableVersion ? (
                <p className="text-[10px] text-fg-3">No promoted model for {scopeLabel} {timeframe}</p>
            ) : null}
        </div>
    );
};

export default ModelVersionSelect;
